module CostAndBilling

open FSharpPlus
open Fable.React
open Fable.React.Props
open Model
open PortalLibs.Client.Util
open Fable.Core
open Fable.Core.JsInterop
open Fulma
open ContractPageModel
open System
open Shared

[<Import("default", from = "react-chartkick")>]
let ReactChartkick: obj = jsNative

importAll "chartkick/chart.js"

[<Import("Chart", from = "chart.js")>]
let Chart: obj = jsNative

Chart?defaults?elements?point?radius <- 0

ReactChartkick?addAdapter Chart

let chartView contractSettings (histories: JobHistory list, forecasts: JobForecast list) isDemo =
  
  let shiftDatesForDemo data min getDate update =
    match (data, isDemo) with
    | [], _ | _, false -> data
    | _ ->
      let peak = data |> (if min then List.minBy else List.maxBy) getDate
      let diff: TimeSpan = DateTime.Now - (getDate peak)
      if diff.TotalDays > 0 then
        data |> List.map(fun x -> (update x ((getDate x).AddDays diff.TotalDays)))
      else
        data
  
  let histories =
    shiftDatesForDemo histories false (fun x -> x.Date) (fun x d -> { x with Date = d })

  let forecasts =
    shiftDatesForDemo forecasts true (fun x -> x.ForecastDate) (fun x d -> { x with ForecastDate = d })
      
  let costData =
    histories
    |> Seq.skipWhile (fun h -> h.Cost = 0.0)
    |>> fun h -> h.Date |> prettyPrintDateDashes, Math.Round(h.Cost)
    |> Seq.toArray

  let billingData =
    histories
    |> Seq.skipWhile (fun h -> h.Billing = 0.0)
    |>> fun h -> h.Date |> prettyPrintDateDashes, Math.Round(h.Billing)
    |> Seq.toArray

  let billingForecastData =
    forecasts
    |> Seq.skipWhile (fun h -> h.BillingForecast = 0m)
    |>> fun h -> h.ForecastDate |> prettyPrintDateDashes, Math.Round(h.BillingForecast)
    |> Seq.toArray

  let varianceData =
    histories
    |> Seq.skipWhile (fun h -> h.Billing = 0.0 && h.Cost = 0.0)
    |>> fun h -> h.Date |> prettyPrintDateDashes, Math.Round(h.Billing - h.Cost)
    |> Seq.toArray

  let inputProps =
    {| prefix = "$"
       thousands = ","
       height = "400px"
       curve = false
       data =
         [| {| name = "Billing"
               data = billingData :> obj
               color = "#00cc00"
               dataset = null :> obj |}
            {| name = "Billing Forecast"
               data = billingForecastData
               color = "#0000cc"
               dataset = {| showLine = contractSettings.CostAndBillingForecasting |} |}
            {| name = "Costs"
               data = costData
               color = "#cc0000"
               dataset = null |}
            {| name = "Variance"
               data = varianceData
               color = "#327da8"
               dataset = {| showLine = false; pointHitRadius = 0 |} |} |]
       library =
         {| ```type`` = "line"
            responsive = true
            hover = {| mode = "nearest"; intersect = true |}
            scales =
              {| y =
                  {| display = true
                     ticks = {| display = true; maxTicksLimit = 6 |}
                     gridLines = {| display = true |} |}
                 x =
                   {| display = true
                      gridLines =
                        {| display = true; color = "rgba(0, 0, 0, 0.1)"; lineWidth = 1; drawOnChartArea = 1 |} |} |}

            animation = {| duration = 1500; easing = "easeOutElastic" |}
            plugins =
              {| tooltip =
                  {| mode = "index"
                     intersect = false
                     displayColors = true
                     xPadding = 10
                     yPadding = 10
                     bodySpacing = 4 |}
                 legend =
                   {| position = "bottom"
                      labels =
                        {| display = true
                           generateLabels =
                             fun _ ->
                               let baseLegend =
                                 [|{| text = "Billing"; fillStyle = "#00cc00"; hidden = false |}
                                   {| text = "Costs"; fillStyle = "#cc0000"; hidden = false |}|]
                               let forecastLegend =
                                 [|{| text = "Billing Forecast"; fillStyle = "#0000cc"; hidden = false |}|]
                               if contractSettings.CostAndBillingForecasting then
                                 Array.append baseLegend forecastLegend
                               else baseLegend
                                 |} |} |}
                 |}
         |}
  ofImport "LineChart" "react-chartkick" inputProps []

let isOver30Days contract =
  let isDateOver30Days (date: DateTime) = DateTime.Now.AddDays(-30.0) > date

  match contract.BillingLastRequestedDate, contract.LastBillDate with
  | None, None -> false
  | Some date, None -> isDateOver30Days date
  | None, Some date -> isDateOver30Days date
  | Some requested, Some billed ->
    if billed >= requested then
      isDateOver30Days billed
    else
      isDateOver30Days requested

let View (model: ContractPageModel.Model) contractSettings isDemo (contract: Contract) dispatch =
  let costAndBillingView data =
    div [ Style [ PaddingBottom "50px" ] ] [
      div [ Style [
              Display DisplayOptions.Flex
              JustifyContent "flex-end"
              AlignItems AlignItemsOptions.Center
            ] ] [
        strong [ Style [
                   yield MarginRight "15px"
                   if isOver30Days contract then
                     yield Color "Red"
                 ] ] [

          (match contract.BillingLastRequestedDate, contract.LastBillDate with
           | None, None -> "Last Billed: never"
           | Some date, None ->
             prettyPrintDateLong date
             |> sprintf "Last Requested: %s"
           | None, Some date ->
             prettyPrintDateLong date
             |> sprintf "Last Billed: %s"
           | Some requested, Some billed ->
             if billed >= requested then
               prettyPrintDateLong billed
               |> sprintf "Last Billed: %s"
             else
               prettyPrintDateLong requested
               |> sprintf "Last Requested: %s")
          |> str
        ]
        Button.button [ Button.OnClick(fun _ -> BillingRequested |> dispatch) ] [
          str "Request Billing"
        ]
      ]
      chartView contractSettings data isDemo
    ]


  let getHistoriesAndForecasts model =
    let histories = Map.tryFind contract.Id model.JobHistories
    let forecasts = Map.tryFind contract.Id model.JobForecasts
    match histories, forecasts with
    | Some hs, Some fs -> Some (hs, fs)
    | Some hs, None when not contractSettings.CostAndBillingForecasting -> Some(hs, [])
    | _, _ -> None


  loadingMessageOrContent
    "Loading Costs and Billing..."
    costAndBillingView (getHistoriesAndForecasts model)